import React, { useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  Stack,
  Paper,
} from '@mui/material';
import { Upload, X, FileText, Image, Film, Music, Check, AlertCircle } from 'lucide-react';
import useMediaUpload from '../../hooks/useMediaUpload';

const CATEGORY_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  document: FileText,
};

const CATEGORY_LABELS = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  document: 'Document',
  all: 'File',
};

/**
 * MediaUpload component for uploading files of various types
 * Uses /api/media/upload endpoint for all media types
 *
 * @param {Object} props
 * @param {'image'|'video'|'audio'|'document'|'all'} [props.category='all'] - Restrict to specific media type
 * @param {Function} [props.onUploadComplete] - Called when upload succeeds with { handle, file, fileName, category }
 * @param {Function} [props.onError] - Called when upload fails with error message
 * @param {Function} [props.onFileSelect] - Called when file is selected (before upload)
 * @param {boolean} [props.autoUpload=true] - Automatically upload when file is selected
 * @param {boolean} [props.showPreview=true] - Show image/video preview
 * @param {boolean} [props.disabled=false] - Disable the upload component
 * @param {string} [props.placeholder] - Custom placeholder text
 * @param {string} [props.buttonText] - Custom button text
 * @param {Object} [props.sx] - Custom styles for the container
 * @param {boolean} [props.compact=false] - Use compact layout
 * @param {string} [props.value] - Controlled value (handle)
 * @param {Function} [props.onChange] - Controlled onChange handler
 */
const MediaUpload = ({
  category = 'all',
  onUploadComplete,
  onError,
  onFileSelect,
  autoUpload = true,
  showPreview = true,
  disabled = false,
  placeholder,
  buttonText,
  sx,
  compact = false,
  value,
  onChange,
}) => {
  const inputRef = useRef(null);

  const {
    file,
    fileName,
    fileCategory,
    previewUrl,
    handle,
    progress,
    uploading,
    error,
    uploaded,
    handleFileInputChange,
    upload,
    reset,
    acceptString,
    canUpload,
  } = useMediaUpload({
    category,
    onSuccess: (result) => {
      onUploadComplete?.(result);
      onChange?.(result.handle);
    },
    onError,
  });

  const handleInputChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onFileSelect?.(selectedFile);
    }
    handleFileInputChange(event, autoUpload);
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      inputRef.current?.click();
    }
  };

  const handleManualUpload = async () => {
    if (canUpload) {
      await upload();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    reset();
    onChange?.('');
  };

  const CategoryIcon = fileCategory ? CATEGORY_ICONS[fileCategory] : Upload;
  const categoryLabel = CATEGORY_LABELS[category] || 'File';
  const displayHandle = value || handle;
  const isUploaded = uploaded || !!value;

  // Compact layout
  if (compact) {
    return (
      <Box sx={sx}>
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            disabled={disabled || uploading}
            startIcon={uploading ? null : <Upload size={16} />}
          >
            {uploading ? 'Uploading...' : buttonText || `Choose ${categoryLabel}`}
          </Button>

          {fileName && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
              {fileName}
            </Typography>
          )}

          {isUploaded && (
            <Check size={16} color="green" />
          )}

          {file && !uploading && (
            <IconButton size="small" onClick={handleRemove}>
              <X size={14} />
            </IconButton>
          )}
        </Stack>

        {uploading && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 1, height: 2, borderRadius: 1 }}
          />
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Full layout with drag-and-drop appearance
  return (
    <Box sx={sx}>
      <input
        ref={inputRef}
        type="file"
        accept={acceptString}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />

      <Paper
        variant="outlined"
        onClick={handleClick}
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: disabled || uploading ? 'default' : 'pointer',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: error ? 'error.main' : isUploaded ? 'success.main' : 'divider',
          bgcolor: isUploaded ? 'success.lighter' : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: disabled || uploading ? 'divider' : 'primary.main',
            bgcolor: disabled || uploading ? 'background.paper' : 'action.hover',
          },
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {/* Preview */}
        {showPreview && previewUrl && fileCategory === 'image' && (
          <Box sx={{ mb: 2, position: 'relative' }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 8,
                objectFit: 'contain',
              }}
            />
            {!uploading && (
              <IconButton
                size="small"
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'error.lighter' },
                }}
              >
                <X size={16} />
              </IconButton>
            )}
          </Box>
        )}

        {showPreview && previewUrl && fileCategory === 'video' && (
          <Box sx={{ mb: 2, position: 'relative' }}>
            <video
              src={previewUrl}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 8,
              }}
            />
            {!uploading && (
              <IconButton
                size="small"
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'error.lighter' },
                }}
              >
                <X size={16} />
              </IconButton>
            )}
          </Box>
        )}

        {/* Icon and Text */}
        {!previewUrl && (
          <>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: isUploaded ? 'success.light' : 'action.selected',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              {error ? (
                <AlertCircle size={24} color="currentColor" />
              ) : isUploaded ? (
                <Check size={24} />
              ) : (
                <CategoryIcon size={24} />
              )}
            </Box>

            {fileName ? (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <Typography variant="body2" fontWeight={500}>
                  {fileName}
                </Typography>
                {!uploading && (
                  <IconButton size="small" onClick={handleRemove}>
                    <X size={14} />
                  </IconButton>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {placeholder || `Drag and drop or click to upload ${categoryLabel.toLowerCase()}`}
              </Typography>
            )}
          </>
        )}

        {/* Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Uploading... {progress}%
            </Typography>
          </Box>
        )}

        {/* Success message */}
        {isUploaded && !uploading && (
          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
            Uploaded successfully
          </Typography>
        )}

        {/* Error message */}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}

        {/* Manual upload button */}
        {!autoUpload && file && !uploaded && !uploading && (
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleManualUpload();
            }}
            sx={{ mt: 2 }}
          >
            Upload
          </Button>
        )}
      </Paper>

      {/* Handle display */}
      {displayHandle && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Handle: {displayHandle}
        </Typography>
      )}
    </Box>
  );
};

export default MediaUpload;
